import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosClient from '../../api/axiosClient';
import { Shield, Lock, CheckSquare, Plus, Trash2, Save } from 'lucide-react';

const RolesPage = () => {
  const queryClient = useQueryClient();
  const [selectedRole, setSelectedRole] = useState(null);
  
  const [roleForm, setRoleForm] = useState({ id: null, name: '', description: '', permissions: [] });

  const { data: roles = [] } = useQuery({ queryKey: ['roles'], queryFn: () => axiosClient.get('/admin/rbac/roles') });
  const { data: permissions = [] } = useQuery({ queryKey: ['permissions'], queryFn: () => axiosClient.get('/admin/rbac/permissions') });

  const saveMutation = useMutation({
    mutationFn: (data) => axiosClient.post('/admin/rbac/roles', data),
    onSuccess: () => { queryClient.invalidateQueries(['roles']); alert("Role Saved!"); setRoleForm({ id: null, name: '', description: '', permissions: [] }); }
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => axiosClient.delete(`/admin/rbac/roles/${id}`),
    onSuccess: () => queryClient.invalidateQueries(['roles'])
  });

  const handleSelectRole = (role) => {
      setSelectedRole(role.id);
      setRoleForm({
          id: role.id, name: role.name, description: role.description,
          permissions: role.permissions.map(p => p.id)
      });
  };

  const togglePermission = (permId) => {
      setRoleForm(prev => {
          const newPerms = prev.permissions.includes(permId) 
             ? prev.permissions.filter(id => id !== permId)
             : [...prev.permissions, permId];
          return { ...prev, permissions: newPerms };
      });
  };

  // ✅ HELPER: Group permissions by Module (e.g., 'orders', 'customers')
  const groupPermissions = () => {
      const groups = {};
      permissions.forEach(p => {
          const [module, action] = p.slug.split(':');
          if (!groups[module]) groups[module] = [];
          groups[module].push({ ...p, action_label: action });
      });
      return groups;
  };

  const permGroups = groupPermissions();

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 animate-fade-in pb-10">
       
       {/* Left: Role List (3 Cols) */}
       <div className="lg:col-span-3 bg-white p-4 rounded-xl border border-slate-200 shadow-sm h-fit">
           <div className="flex justify-between items-center mb-4">
              <h2 className="font-bold text-slate-700 flex items-center"><Shield className="w-5 h-5 mr-2 text-blue-600"/> Roles</h2>
              <button onClick={() => { setSelectedRole(null); setRoleForm({ id: null, name: '', description: '', permissions: [] }); }} className="p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100"><Plus className="w-4 h-4"/></button>
           </div>
           <div className="space-y-2">
              {roles.map(r => (
                  <div key={r.id} onClick={() => handleSelectRole(r)} className={`p-3 rounded-lg border cursor-pointer flex justify-between items-center ${selectedRole === r.id ? 'bg-blue-50 border-blue-200 ring-1 ring-blue-200' : 'bg-white border-slate-100 hover:bg-slate-50'}`}>
                      <div>
                          <h4 className="font-bold text-sm text-slate-700 capitalize">{r.name.replace('_', ' ')}</h4>
                          <p className="text-[10px] text-slate-400">{r.permissions.length} access points</p>
                      </div>
                      {!['super_admin', 'customer'].includes(r.name) && (
                          <button onClick={(e) => { e.stopPropagation(); if(window.confirm('Delete role?')) deleteMutation.mutate(r.id); }} className="text-slate-300 hover:text-red-500"><Trash2 className="w-3 h-3"/></button>
                      )}
                  </div>
              ))}
           </div>
       </div>

       {/* Right: Permission Matrix (9 Cols) */}
       <div className="lg:col-span-9 bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
           <div className="flex justify-between items-center mb-6 border-b border-slate-100 pb-4">
               <div>
                   <h3 className="font-bold text-lg text-slate-800">{roleForm.id ? `Edit ${roleForm.name}` : 'Create New Role'}</h3>
                   <p className="text-sm text-slate-500">Configure granular access control.</p>
               </div>
               <button onClick={() => saveMutation.mutate(roleForm)} className="bg-emerald-600 text-white px-5 py-2 rounded-lg font-bold flex items-center hover:bg-emerald-700 shadow-lg shadow-emerald-500/20">
                   <Save className="w-4 h-4 mr-2"/> Save Changes
               </button>
           </div>

           <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
               <div><label className="text-xs font-bold text-slate-500 uppercase">Role Name</label><input className="w-full p-2.5 border rounded-lg" value={roleForm.name} onChange={e => setRoleForm({...roleForm, name: e.target.value})} disabled={['super_admin', 'customer'].includes(roleForm.name)} /></div>
               <div><label className="text-xs font-bold text-slate-500 uppercase">Description</label><input className="w-full p-2.5 border rounded-lg" value={roleForm.description} onChange={e => setRoleForm({...roleForm, description: e.target.value})} /></div>
           </div>

           {/* ✅ THE MATRIX UI */}
           <h4 className="font-bold text-slate-700 mb-4 flex items-center"><Lock className="w-4 h-4 mr-2"/> Access Permissions</h4>
           
           <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
               {Object.entries(permGroups).map(([module, perms]) => (
                   <div key={module} className="border border-slate-200 rounded-xl overflow-hidden">
                       <div className="bg-slate-50 px-4 py-2 border-b border-slate-200 font-bold text-sm text-slate-700 uppercase flex justify-between items-center">
                           {module}
                           {/* Quick Select All for Module */}
                           <button 
                                onClick={() => {
                                    const allIds = perms.map(p => p.id);
                                    const allSelected = allIds.every(id => roleForm.permissions.includes(id));
                                    setRoleForm(prev => ({
                                        ...prev,
                                        permissions: allSelected 
                                            ? prev.permissions.filter(id => !allIds.includes(id)) // Deselect All
                                            : [...new Set([...prev.permissions, ...allIds])] // Select All
                                    }));
                                }}
                                className="text-[10px] text-blue-600 hover:underline"
                           >
                               Toggle All
                           </button>
                       </div>
                       <div className="p-3 space-y-2">
                           {perms.map(p => (
                               <label key={p.id} className="flex items-center space-x-3 cursor-pointer group">
                                   <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${roleForm.permissions.includes(p.id) ? 'bg-blue-600 border-blue-600' : 'border-slate-300 group-hover:border-blue-400'}`}>
                                       {roleForm.permissions.includes(p.id) && <CheckSquare className="w-3 h-3 text-white" />}
                                   </div>
                                   <input type="checkbox" className="hidden" checked={roleForm.permissions.includes(p.id)} onChange={() => togglePermission(p.id)} />
                                   <span className={`text-sm ${roleForm.permissions.includes(p.id) ? 'text-slate-800 font-medium' : 'text-slate-500'}`}>
                                       {p.description}
                                   </span>
                               </label>
                           ))}
                       </div>
                   </div>
               ))}
           </div>

       </div>
    </div>
  );
};

export default RolesPage;